import { useEffect, useState } from 'react';
import { Icon } from '../utils/icons';
import { getDesignTokens } from '../utils/designTokens';
import { useThemeStore } from '../store/themeStore';
import LoadingSpinner from './LoadingSpinner';
import type { Toast as ToastType } from '../store/toastStore';

interface ToastProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

export default function Toast({ toast, onDismiss }: ToastProps) {
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const toastStyles = tokens.toast[toast.type];
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!toast.duration) return undefined;
    const timer = window.setTimeout(() => {
      setIsExiting(true);
      // Wait for slide-out animation to complete before dismissing
      setTimeout(() => {
        onDismiss(toast.id);
      }, 300); // Match animation duration
    }, toast.duration);
    return () => window.clearTimeout(timer);
  }, [toast, onDismiss]);

  return (
    <div
      className={`flex items-center gap-2 ${toastStyles.bg} ${toastStyles.border} border rounded-full ${tokens.card.shadow} px-3 py-1.5 min-h-[32px] max-w-fit`}
      role="status"
      style={{
        animation: isExiting ? 'slideOutRight 0.3s ease-in forwards' : 'slideInRight 0.3s ease-out',
      }}
    >
      {toast.type === 'loading' ? (
        <LoadingSpinner size="sm" />
      ) : (
        <Icon
          name={toast.type === 'error' ? 'warning' : toast.type === 'success' ? 'success' : 'checklist'}
          size="sm"
          className={toastStyles.text}
        />
      )}
      <span className={`text-xs font-medium ${toastStyles.text} whitespace-nowrap`}>{toast.message}</span>
    </div>
  );
}
